import "./styles.scss"
import React, { FC, useMemo, useState, useCallback } from "react"
import { AutoSizer, Column, Table } from "react-virtualized"
import { Box, IconButton, Button, Modal } from "@material-ui/core"
import { Community, Event, User } from "../../../../types"
import { Badge, Game } from "@smerf-gaming/types"
import { Edit, Loyalty, Lock } from "@material-ui/icons"
import "react-virtualized/styles.css"
import { TABS } from "../tabs"
import { TableCellRenderer } from "react-virtualized/dist/es/Table"
import { EditItemModal } from "../edit-item-modal"
import { ConfirmationVerify } from "../confirmation-verify-modal"
import { AddUserBadgeModal } from "../add-user-badge-modal"
import { GameRolesModal } from "../game-role-modal"
import { EventRolesModal } from "../event-role-modal"
import { UpdateUserPasswordModal } from "../update-user-password-modal"

export type SearchListData = User[] | Community[] | Event[] | Badge[] | Game[]
export type SearchListItem = User | Community | Event | Badge | Game

interface ListProps {
  data: SearchListData
  tab: TABS
  fetchData: () => void
}

export const List: FC<ListProps> = ({ data, tab, fetchData }) => {
  const [visibleModal, setVisibleModal] = useState<boolean>(false)
  const [confirmationVerifyModal, setConfirmationVerifyModal] = useState<boolean>(false)
  const [userBadgeModalVisible, setUserBadgeModalVisible] = useState<boolean>(false)
  const [userPasswordModalVisible, setUserPasswordModalVisible] = useState<boolean>(false)
  const [selectedListItem, setListItem] = useState<SearchListItem | null>(null)
  const [gameRoleModalVisible, setGameRoleModalVisible] = useState<boolean>(false)
  const [eventRoleModalVisible, setEventRoleModalVisible] = useState<boolean>(false)

  const openEditModal = (listItem: SearchListItem) => {
    setListItem(listItem)
    handleOpenModal()
  }

  const openVerifyModal = (listItem: SearchListItem) => {
    setListItem(listItem)
    handleOpenVerify()
  }

  const openUserBadgesModal = (listItem: SearchListItem) => {
    setListItem(listItem)
    handleOpenUserBadgeModal()
  }

  const openUserPasswordModal = (listItem: SearchListItem) => {
    setListItem(listItem)
    handleOpenUserPasswordModal()
  }

  const openGameRoleModal = (listItem: SearchListItem) => {
    setListItem(listItem)
    handleOpenGameRoleModal()
  }

  const openEventRoleModal = (listItem: SearchListItem) => {
    setListItem(listItem)
    handleOpenEventRoleModal()
  }

  const rowGetter = useCallback(({ index }: { index: number }) => data[index], [data])
  const renderPicture: TableCellRenderer = ({ cellData }) => <img src={cellData} className="picture" alt="" />
  const renderUserButtons: TableCellRenderer = cell => (
    <div>
      <IconButton aria-label="Edit" onClick={() => openEditModal(cell.rowData)}>
        <Edit />
      </IconButton>
      <IconButton aria-label="Badges" onClick={() => openUserBadgesModal(cell.rowData)}>
        <Loyalty />
      </IconButton>
      <IconButton aria-label="Password" onClick={() => openUserPasswordModal(cell.rowData)}>
        <Lock />
      </IconButton>
      <Button variant="contained" size="small" onClick={() => openVerifyModal(cell.rowData)}>
        {cell.rowData.verified ? "Un-Verify" : "Verify"}
      </Button>
    </div>
  )
  const renderEdit: TableCellRenderer = cell => (
    <IconButton aria-label="Edit" onClick={() => openEditModal(cell.rowData)}>
      <Edit />
    </IconButton>
  )
  const renderAddHiddenGameRole: TableCellRenderer = cell => (
    <Button
      disabled={!cell.rowData.hidden}
      variant="contained"
      size="small"
      onClick={() => openGameRoleModal(cell.rowData)}>
      {"Hidden Roles"}
    </Button>
  )

  const renderAddEventRole: TableCellRenderer = cell => (
    <Button variant="contained" size="small" onClick={() => openEventRoleModal(cell.rowData)}>
      {"Roles"}
    </Button>
  )

  const tableProps = useMemo(
    () => ({
      headerHeight: 44,
      rowHeight: 66,
      height: 500,
      rowClassName: "table-row",
      headerClassName: "table-header",
      rowCount: data.length,
      rowGetter: rowGetter,
    }),
    [data.length, rowGetter],
  )

  const handleOpenModal = () => setVisibleModal(true)
  const handleCloseModal = () => setVisibleModal(false)

  const handleOpenVerify = () => setConfirmationVerifyModal(true)
  const handleCloseVerify = () => setConfirmationVerifyModal(false)

  const handleOpenUserBadgeModal = () => setUserBadgeModalVisible(true)
  const handleCloseUserBadgeModal = () => setUserBadgeModalVisible(false)

  const handleOpenUserPasswordModal = () => setUserPasswordModalVisible(true)
  const handleCloseUserPasswordModal = () => setUserPasswordModalVisible(false)

  const handleOpenGameRoleModal = () => setGameRoleModalVisible(true)
  const handleCloseGameRoleModal = () => setGameRoleModalVisible(false)

  const handleOpenEventRoleModal = () => setEventRoleModalVisible(true)
  const handleCloseEventRoleModal = () => setEventRoleModalVisible(false)

  return (
    <Box className="box-container" boxShadow={1}>
      <Modal open={visibleModal} onClose={handleCloseModal} className="modal">
        <EditItemModal listItem={selectedListItem} tab={tab} fetchData={fetchData} hideModal={handleCloseModal} />
      </Modal>
      <Modal open={confirmationVerifyModal} onClose={handleCloseVerify} className="modal">
        <ConfirmationVerify listItem={selectedListItem as User} fetchData={fetchData} hideModal={handleCloseVerify} />
      </Modal>
      <Modal open={userBadgeModalVisible} onClose={handleCloseUserBadgeModal} className="modal">
        <AddUserBadgeModal
          listItem={selectedListItem as User}
          fetchData={fetchData}
          hideModal={handleCloseUserBadgeModal}
        />
      </Modal>
      <Modal open={userPasswordModalVisible} onClose={handleCloseUserPasswordModal} className="modal">
        <UpdateUserPasswordModal
          listItem={selectedListItem as User}
          fetchData={fetchData}
          hideModal={handleCloseUserPasswordModal}
        />
      </Modal>
      <Modal open={gameRoleModalVisible} onClose={handleCloseGameRoleModal} className="modal">
        <GameRolesModal
          listItem={selectedListItem as Game}
          fetchData={fetchData}
          hideModal={handleCloseGameRoleModal}
        />
      </Modal>
      <Modal open={eventRoleModalVisible} onClose={handleCloseEventRoleModal} className="modal">
        <EventRolesModal
          listItem={selectedListItem as Event}
          fetchData={fetchData}
          hideModal={handleCloseEventRoleModal}
        />
      </Modal>
      {tab === TABS.USERS && (
        <AutoSizer disableHeight>
          {({ width }) => (
            <Table width={width} {...tableProps}>
              <Column dataKey="profilePicUrl" width={100} cellRenderer={renderPicture} />
              <Column dataKey="id" label="id" width={200} />
              <Column dataKey="username" label="Username" width={150} />
              <Column
                dataKey="button"
                width={50}
                cellRenderer={renderUserButtons}
                className="buttons-container"
                flexGrow={1}
              />
            </Table>
          )}
        </AutoSizer>
      )}

      {tab === TABS.EVENTS && (
        <AutoSizer disableHeight>
          {({ width }) => (
            <Table width={width} {...tableProps}>
              <Column dataKey="img_url" width={100} cellRenderer={renderPicture} />
              <Column dataKey="id" label="id" width={200} />
              <Column dataKey="name" label="Name" width={150} />
              <Column dataKey="details" label="Details" width={200} />
              <Column dataKey="edit" width={50} cellRenderer={renderEdit} className="buttons-container" flexGrow={1} />
              <Column
                dataKey="button"
                width={50}
                cellRenderer={renderAddEventRole}
                className="buttons-container"
                flexGrow={1}
              />
            </Table>
          )}
        </AutoSizer>
      )}

      {tab === TABS.COMMUNITIES && (
        <AutoSizer disableHeight>
          {({ width }) => (
            <Table width={width} {...tableProps}>
              <Column dataKey="img_url" width={100} cellRenderer={renderPicture} />
              <Column dataKey="id" label="id" width={200} />
              <Column dataKey="name" label="Name" width={150} />
              <Column dataKey="description" label="Description" width={200} />
              <Column dataKey="edit" width={50} cellRenderer={renderEdit} className="buttons-container" flexGrow={1} />
            </Table>
          )}
        </AutoSizer>
      )}

      {tab === TABS.BADGES && (
        <AutoSizer disableHeight>
          {({ width }) => (
            <Table width={width} {...tableProps}>
              <Column dataKey="img_url" width={100} cellRenderer={renderPicture} />
              <Column dataKey="id" label="id" width={200} />
              <Column dataKey="display_name" label="Display Name" width={150} />
              <Column dataKey="description" label="Description" width={200} />
            </Table>
          )}
        </AutoSizer>
      )}

      {tab === TABS.GAMES && (
        <AutoSizer disableHeight>
          {({ width }) => (
            <Table width={width} {...tableProps}>
              <Column dataKey="img_src" width={100} cellRenderer={renderPicture} />
              <Column dataKey="id" label="id" width={200} />
              <Column dataKey="name" label="Name" width={300} />
              <Column dataKey="hidden" label="Hidden" width={300} />
              <Column
                dataKey="button"
                width={140}
                cellRenderer={renderAddHiddenGameRole}
                className="buttons-container"
                flexGrow={1}
              />
            </Table>
          )}
        </AutoSizer>
      )}
    </Box>
  )
}
